import React, { useEffect, useState } from "react";
import { Divider, Typography,
  CircularProgress } from "@mui/material";
import PayaAddCardForm from "../../../../components/PayaAddCard/PayaAddCardForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import AxiosInstance from "../../../AxiosInstance";
import CardPayment from "../../../Admin/Invoice/Payment/CardPayment";
import { Button } from "reactstrap";
import { Grid } from "@mui/material";
import { LoaderComponent } from "../../../../components/Icon/Index";
import showToast from "../../../../components/Toast/Toster";
import DollerInput from "../../../../components/InputFields/Doller";
import { useUserType } from "../../../../contexts/UserTypeContext";

const InvoiceLivePayment = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const { token } = {
    token: queryParams.get("token"),
  };
  const { userType, logo } = useUserType();
  const isDonationMode = userType === "donor";

  const [selectedCard, setSelectedCard] = useState(undefined);
  const [loader, setLoader] = useState(true);
  const [creditFeePercentage, setCreditFeePercentage] = useState(0);
  const [debitFeePercentage, setDebitFeePercentage] = useState(0);
  const [cardFeeConfig, setCardFeeConfig] = useState(null);
  const [tokenData, setTokenData] = useState({
    company: null,
    customer: null,
    recurring: null,
    companyProfile: null,
    customerProfile: null,
    transactionFees: null,
    userProfile: null,
  });
  const [isDataReady, setIsDataReady] = useState(false);
  const [transactionFee, setTransactionFee] = useState(0);
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");

  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  // Do NOT call handleAuth() here - this page is public (customer opens via email link token).
  // Calling handleAuth() with no auth token would redirect to login with "Token not found. Please log in."

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("Missing token");
        setLoader(false);
        return;
      }
      setLoader(true);
      try {
        const response = await AxiosInstance.get(
          `v1/recurring-payment/RecurringPayment`,
          {
            params: {
              token: token,
              _t: Date.now(), // cache-bust so server always returns 200 with body (no 304)
            },
          }
        );
        
        if (response?.data?.data) {
          const data = {
            company: {
              ...response?.data?.data?.company,
              companyName: response?.data?.data?.companyProfile?.CompanyName,
              phoneNumber: response?.data?.data?.companyProfile?.PhoneNumber,
              primaryEmailAddress: response?.data?.data?.company?.EmailAddress,
              companyId: response?.data?.data?.company?.CompanyId,
              IsDonationMode: response?.data?.data?.company?.IsDonationMode,
              transactionFeePercentage:
                response?.data?.data?.company?.transactionFeePercentage,
              Gateway: response?.data?.data?.company?.Gateway,
            },
            customer: {
              ...response?.data?.data?.customer,
              FirstName: response?.data?.data?.customerProfile?.FirstName,
              LastName: response?.data?.data?.customerProfile?.LastName,
              CustomerId: response?.data?.data?.customer?.UserId,
              EmailAddress: response?.data?.data?.customer?.EmailAddress,
              PhoneNumber: response?.data?.data?.customerProfile?.PhoneNumber,
            },
            transactionFees: response?.data?.data?.transactionFees,
            userProfile: {
              ...response?.data?.data?.userProfile,
              TransactionFee: response?.data?.data?.userProfile?.TransactionFee,
            },

            gatewayKeys: response?.data?.data?.gatewayKeys,
            recurring: response?.data?.data?.recurring,
            cardFeeConfig: response?.data?.data?.cardFeeConfig,
            creditCardEnabled: response?.data?.data?.creditCardEnabled,
            achEnabled: response?.data?.data?.achEnabled,
          };
          setTokenData(data);
          
          // Set card fee config from response
          if (response?.data?.data?.cardFeeConfig) {
            setCardFeeConfig(response.data.data.cardFeeConfig);
          } else {
            // Set default if no config found
            setCardFeeConfig({
              FeeType: "Percentage",
              FeeAmount: 0,
              PricingStructure: "Cash Discount",
              FeeLabel: "",
            });
          }
          
          if (data.transactionFees) {
            setCreditFeePercentage(
              data.transactionFees.creditFeePercentage || 0
            );
            setDebitFeePercentage(data.transactionFees.debitFeePercentage || 0);
          }

          if (data.company?.companyId && data.customer?.CustomerId) {
            setIsDataReady(true);
          }
        }
      } catch (err) {
        console.error("Token validation error:", err);  
        const msg = err?.response?.data?.message;
        const isExpiredOrInvalid = err?.response?.status === 403;
        setError(
          msg ||
          (isExpiredOrInvalid
            ? "This payment link is invalid or has expired. Please request a new payment link from the company."
            : "An error occurred")
        );
      } finally {
        setLoader(false);
      }
    };

    validateToken();
  }, [token]);

  // Card fee config is now included in the token response, no need for separate API call

  const [buttonLoader, setButtonLoader] = useState(false);

  const [error, setError] = useState("");

  useEffect(() => {
    let fee = 0;
    const baseAmount = parseFloat(tokenData?.recurring?.amount || 0);
    
    // Check if transaction fee should be applied
    const shouldApplyFee = 
      tokenData?.userProfile?.TransactionFee &&
      selectedCard &&
      baseAmount > 0 &&
      cardFeeConfig?.FeeAmount > 0;

    if (shouldApplyFee) {
      const cardType = (
        selectedCard?.cardType ||
        selectedCard?.["@attributes"]?.cardType ||
        ""
      ).toUpperCase();

      // Use card fee config if available, otherwise fall back to old percentage method
      if (cardFeeConfig && cardFeeConfig.FeeAmount > 0) {
        if (cardFeeConfig.FeeType === "Percentage") {
          fee = (baseAmount * cardFeeConfig.FeeAmount) / 100;
        } else if (cardFeeConfig.FeeType === "Fixed") {
          fee = cardFeeConfig.FeeAmount;
        }
      } else {
        // Fallback to old percentage method
        let feePercentage =
          cardType === "DEBIT" ? debitFeePercentage : creditFeePercentage;
        if (!isNaN(baseAmount) && !isNaN(feePercentage) && feePercentage > 0) {
          fee = (baseAmount * feePercentage) / 100;
        }
      }
    }

    setTransactionFee(fee);
  }, [tokenData, selectedCard, debitFeePercentage, creditFeePercentage, cardFeeConfig]);

  useEffect(() => {
    if (
      tokenData?.userProfile?.TransactionFee &&
      selectedCard &&
      transactionFee > 0
    ) {
      const baseAmount = parseFloat(tokenData?.recurring?.amount || 0);
      const cardType = (
        selectedCard?.cardType ||
        selectedCard?.["@attributes"]?.cardType ||
        ""
      ).toUpperCase();

      // Use card fee config if available
      let feePercentage = 0;
      if (cardFeeConfig && cardFeeConfig.FeeAmount > 0) {
        feePercentage = cardFeeConfig.FeeAmount;
      } else {
        // Fallback to old percentage method
        feePercentage =
          cardType === "DEBIT" ? debitFeePercentage : creditFeePercentage;
      }

      paymentFormik.setFieldValue("transactionFeeDetails", {
        originalAmount: baseAmount.toFixed(2),
        feeAmount: transactionFee.toFixed(2),
        feePercentage: feePercentage,
        totalAmount: (baseAmount + transactionFee).toFixed(2),
        cardType: cardType,
        pricingStructure: cardFeeConfig?.PricingStructure || "Cash Discount",
        feeType: cardFeeConfig?.FeeType || "Percentage",
      });
    } else {
      paymentFormik.setFieldValue("transactionFeeDetails", null);
    }
  }, [transactionFee, selectedCard, cardFeeConfig]);

  // Flags from API (gateway-keys IsDelete: false): hide Card when disabled
  const creditCardEnabled = tokenData?.creditCardEnabled !== false;
  const achEnabled = tokenData?.achEnabled === true;

  const paymentFormik = useFormik({
    initialValues: {
      method: "Credit/debit card",
      amount: tokenData?.recurring?.amount || "",
      date: dayjs(new Date()).format("YYYY-MM-DD"),
      customer_vault_id: tokenData?.recurring?.customer_vault_id || "",
      billing_id: tokenData?.recurring?.billing_id || "",
      cheque_number: "",
      transactionFeeDetails: null,
      // Add fields for Dejavoo
      token: tokenData?.recurring?.token || "",
      Gateway: tokenData?.gatewayKeys?.activeGateway || "",
      paymentDetails: {
        token: tokenData?.recurring?.token || "",
        Gateway: tokenData?.gatewayKeys?.activeGateway || "",
        billing_id: tokenData?.recurring?.billing_id || "",
        customer_vault_id: tokenData?.recurring?.customer_vault_id || "",
      },
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      method: Yup.string().required("This field is required."),
      amount: Yup.number()
        .required("This field is required.")
        .min(1, "The amount must be at least 1.")
        .max(
          tokenData?.recurring?.amount,
          `The amount must be less than or equal to ${tokenData?.recurring?.amount}.`
        ),
    }),
    onSubmit: async (values) => {
      setButtonLoader(true);
      if (!values?.date) {
        setError("Please select date");
        setButtonLoader(false);
        return;
      }

      try {
        const originalAmount = parseFloat(values?.amount);
        let totalAmount = originalAmount;
        
        // Calculate total amount with fee if applicable
        // For Surcharge: total = original + fee
        // For Cash Discount: total = original + fee (same calculation, but displayed differently)
        if (
          tokenData?.userProfile?.TransactionFee &&
          transactionFee > 0 &&
          paymentFormik?.values?.transactionFeeDetails
        ) {
          totalAmount = parseFloat(paymentFormik.values.transactionFeeDetails.totalAmount || originalAmount);
        }

        // Backend expects paymentData.paymentToken for Dejavoo card-on-file
        const gatewayRaw = values?.Gateway || tokenData?.gatewayKeys?.activeGateway || tokenData?.company?.Gateway;
        const isDejavoo = (gatewayRaw || "").toString().toUpperCase() === "DEJAVOO";
        const cardToken = values?.token || selectedCard?.token || tokenData?.recurring?.token;

        if (isDejavoo && !cardToken) {
          setError("Please select a card to pay with.");
          setButtonLoader(false);
          return;
        }

        // Dejavoo billing_id/customer_vault_id can be strings; avoid NaN
        const billingId = values?.billing_id != null && typeof values.billing_id === "string" && values.billing_id.trim() !== ""
          ? values.billing_id
          : Number(values?.billing_id);
        const customerVaultId = values?.customer_vault_id != null && typeof values.customer_vault_id === "string" && values.customer_vault_id.trim() !== ""
          ? values.customer_vault_id
          : Number(values?.customer_vault_id);

        // Build payment data object - MUST use paymentData (not paymentDetails)
        const paymentObject = {
          paymentData: {
            billing_id: billingId,
            customer_vault_id: customerVaultId,
            date: values?.date,
            company: tokenData?.company?.companyName,
            amount: Number(totalAmount.toFixed(2)),
            CompanyId: tokenData?.company?.companyId,
            CustomerId: tokenData?.customer?.CustomerId,
            paymentMethod: "card_on_file",
            paymentDoingFor: "Invoice",
            paymentSource: "Link Payment",
            method: "Card",
            recurringId: tokenData?.recurring?.recurringId,
            nextDueDate: tokenData?.recurring?.nextDueDate,
            frequency: tokenData?.recurring?.frequency,
            frequency_interval: tokenData?.recurring?.frequency_interval || "1",
            day_of_month: tokenData?.recurring?.day_of_month,
            weekday: tokenData?.recurring?.weekday,
            month: tokenData?.recurring?.month,
            day_of_year: tokenData?.recurring?.day_of_year,
            days_after_quarter: tokenData?.recurring?.days_after_quarter,
            description:
              tokenData?.recurring?.description || "Recurring Payment",
            IsDonationMode: tokenData?.company?.IsDonationMode,
            IsTransactionFeePercentage:
              tokenData?.recurring?.IsTransactionFeePercentage,
            transactionFee:
              paymentFormik?.values?.transactionFeeDetails?.feeAmount || 0,
            transactionFeeDetails: paymentFormik?.values?.transactionFeeDetails,
            token: cardToken || values?.token || tokenData?.recurring?.token,
            Gateway: values?.Gateway || tokenData?.gatewayKeys?.activeGateway || tokenData?.company?.Gateway,
            ...(isDejavoo && cardToken ? { paymentToken: cardToken } : {}),
          },
        };
        const res = await AxiosInstance.post(
          "v1/payment/process-payment",
          paymentObject
        );

        // Check for success in multiple ways
        const isSuccess = 
          res?.data?.success === true || 
          res?.data?.statusCode === 100 || 
          res?.data?.status === 200 ||
          (res?.data?.statusCode === 200 && res?.data?.success !== false);

        if (isSuccess) {
          showToast.success(
            res?.data?.message || res?.data?.data?.responseMessage || res?.data?.data?.responsetext || ""
          );
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          const msg = res?.data?.message || res?.data?.data?.message || res?.data?.data?.responsetext || res?.data?.error || "";
          setButtonLoader(false);
          showToast.error(msg || "");
          paymentFormik?.resetForm();
        }
      } catch (error) {
        const errorMessage =
          typeof error === "string" ? error : (error?.response?.data?.message || error?.response?.data?.data?.message || error?.response?.data?.data?.responsetext || error?.response?.data?.data?.error || error?.response?.data?.error || error?.message || "");
        setButtonLoader(false);
        showToast.error(errorMessage || "");
        paymentFormik?.resetForm();
      } finally {
        setButtonLoader(false);
      }
    },
  });

  useEffect(() => {
    const updateError = () => {
      if (error) {
        if (
          paymentFormik?.values?.date &&
          (paymentFormik?.values?.method === "ACH bank payment" ||
            paymentFormik?.values?.method === "Credit/debit card") &&
          paymentFormik?.values?.billing_id
        ) {
          setError("");
        } else {
          if (paymentFormik?.values?.method === "Credit/debit card") {
            setError("Plase select card");
          } else if (paymentFormik?.values?.method === "ACH bank payment") {
            setError("Plase select ACH account");
          } else {
            setError("");
          }
        }
      }
    };
    updateError();
  }, [paymentFormik]);

  useEffect(() => {
    if (tokenData?.recurring?.amount) {
      paymentFormik?.setFieldValue("amount", tokenData?.recurring?.amount);
    }
  }, [tokenData]);

  const handleCardChange = (newCard) => {
    setSelectedCard(newCard);

    const cardType = (
      newCard?.cardType ||
      newCard?.["@attributes"]?.cardType ||
      ""
    ).toUpperCase();

    const currentAmount = parseFloat(tokenData?.recurring?.amount || 0);
    
    if (tokenData?.userProfile?.TransactionFee && currentAmount > 0 && cardFeeConfig?.FeeAmount > 0) {
      let feeAmount = 0;
      let feePercentage = 0;
      
      // Use card fee config if available
      if (cardFeeConfig && cardFeeConfig.FeeAmount > 0) {
        feePercentage = cardFeeConfig.FeeAmount;
        if (cardFeeConfig.FeeType === "Percentage") {
          feeAmount = (currentAmount * cardFeeConfig.FeeAmount) / 100;
        } else if (cardFeeConfig.FeeType === "Fixed") {
          feeAmount = cardFeeConfig.FeeAmount;
        }
      } else {
        // Fallback to old percentage method
        feePercentage = cardType === "DEBIT" ? debitFeePercentage : creditFeePercentage;
        feeAmount = (currentAmount * feePercentage) / 100;
      }

      const totalAmount = currentAmount + feeAmount;

      paymentFormik.setFieldValue("transactionFeeDetails", {
        originalAmount: currentAmount.toFixed(2),
        feeAmount: feeAmount.toFixed(2),
        feePercentage: feePercentage,
        totalAmount: totalAmount.toFixed(2),
        cardType: cardType,
        pricingStructure: cardFeeConfig?.PricingStructure || "Cash Discount",
        feeType: cardFeeConfig?.FeeType || "Percentage",
      });

      setTransactionFee(feeAmount);
    } else {
      paymentFormik.setFieldValue("transactionFeeDetails", null);
      setTransactionFee(0);
    }
  };

  const CycleData = () => {
    const freq = tokenData?.recurring?.frequency;
    const interval = tokenData?.recurring?.frequency_interval;
    if (
      (freq?.toLowerCase() === "every n months" ||
        freq?.toLowerCase() === "every n weeks") &&
      interval != null &&
      interval !== ""
    ) {
      return freq?.replace("Every N", `Every ${interval}`);
    }
    return freq || "N/A";
  };

  if (loader)
    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <LoaderComponent variant="skeleton" loader={loader} height="50" width="50" />
      </Grid>
    );
  else
    return (
      <Grid
        style={{
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        {/* Modern Header */}
        <Grid
          style={{
            width: "100%",
            backgroundColor: isDonationMode
              ? "var(--bg-white-donor-color)"
              : "var(--bg-blue-color)",
            padding: "24px 0",
          }}
        >
          <Grid
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "0 40px",
            }}
          >
            <img
              src={logo}
              alt="Company Logo"
              style={{
                height: isDonationMode ? "60px" : "45px",
                width: "auto",
                maxWidth: "250px",
                backgroundColor: isDonationMode ? "none" : "transparent",
                padding: isDonationMode ? "8px 16px" : "0",
              }}
            />
          </Grid>
        </Grid>

        {/* Main Content - Modern Layout */}
        <Grid
          style={{
            maxWidth: "1000px",
            margin: "60px auto",
            padding: "0 40px",
          }}
        >
          {tokenData?.recurring?.amount !== "0.00" ? (
            <div>
              {/* Simple Title Section */}
              <div
                style={{
                  marginBottom: "32px",
                }}
              >
                <Typography
                  style={{
                    color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                    fontSize: "22px",
                    fontWeight: 500,
                    marginBottom: "6px",
                  }}
                >
                  Complete Your Payment
                </Typography>
                <Typography
                  style={{
                    color: "#64748b",
                    fontSize: "14px",
                    fontWeight: 400,
                  }}
                >
                  {tokenData?.customer?.FirstName} {tokenData?.customer?.LastName}
                </Typography>
              </div>

              {/* Simple Payment Summary */}
              <div
                style={{
                  backgroundColor: "var(--bg-white-color)",
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <Typography
                      style={{
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 400,
                        marginBottom: "4px",
                      }}
                    >
                      Payment Frequency
                    </Typography>
                    <Typography
                      style={{
                        color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      {CycleData()}
                      {tokenData?.recurring?.frequency === "Monthly" && (
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 400,
                            color: "#64748b",
                          }}
                        >
                          {" "}
                          (Day {tokenData?.recurring?.day_of_month || "-"})
                        </span>
                      )}
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      style={{
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 400,
                        marginBottom: "4px",
                      }}
                    >
                      Next Payment Date
                    </Typography>
                    <Typography
                      style={{
                        color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      {tokenData?.recurring?.nextDueDate
                        ? formatDateWithUserTimezone(tokenData?.recurring?.nextDueDate, {
                            dateFormat: dateFormat,
                            excludeTime: true
                          })
                        : "-"}
                    </Typography>
                  </div>
                </div>

                <div
                  style={{
                    paddingTop: "16px",
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <Typography
                    style={{
                      color: "#64748b",
                      fontSize: "12px",
                      fontWeight: 400,
                      marginBottom: "4px",
                    }}
                  >
                    Payment Amount
                  </Typography>
                  <Typography
                    style={{
                      color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                      fontSize: "20px",
                      fontWeight: 600,
                    }}
                  >
                    ${new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(tokenData?.recurring?.amount) || 0)}
                  </Typography>
                </div>
              </div>

              {/* Card Selection - only show when credit card is enabled for company */}
              {creditCardEnabled ? (
              <div style={{ marginBottom: "24px" }}>
                {isDataReady ? (
                  <>
                    {!(tokenData?.gatewayKeys?.activeGateway || tokenData?.company?.Gateway) ? (
                      <div>Loading company data...</div>
                    ) : (tokenData?.gatewayKeys?.activeGateway || tokenData?.company?.Gateway || "").toString().toUpperCase() === "PAYA" ? (
                      <PayaAddCardForm
                        onSuccess={(cardData) => {
                          setSelectedCard(cardData);
                        }}
                        onCancel={() => {}}
                        CustomerId={tokenData?.customer?.CustomerId}
                        CompanyId={tokenData?.company?.CompanyId}
                      />
                    ) : (
                      <CardPayment
                        paymentFormik={paymentFormik}
                        selectedCard={selectedCard}
                        setSelectedCard={setSelectedCard}
                        CustomerId={tokenData?.customer?.CustomerId}
                        CompanyId={tokenData?.company?.CompanyId}
                        tokenDecode={{
                          Gateway: (tokenData?.gatewayKeys?.activeGateway || tokenData?.company?.Gateway || "").toString().toUpperCase(),
                          CompanyId: tokenData?.company?.companyId || tokenData?.company?.CompanyId,
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Grid
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "40px",
                    }}
                  >
                    <LoaderComponent variant="skeleton" loader={true} height="50" width="50" />
                  </Grid>
                )}
              </div>
              ) : (
              <div style={{ marginBottom: "24px", padding: "16px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <Typography style={{ color: "#B45309", fontSize: "14px", fontWeight: 500 }}>
                  For this company, credit/debit card is disabled. Card payments are not accepted for this organization.
                </Typography>
              </div>
              )}

              {error && (
                <Typography
                  style={{
                    color: "var(--bg-red-color)",
                    marginBottom: "24px",
                  }}
                >
                  {error}
                </Typography>
              )}

              {/* Simple Fee Breakdown */}
              {tokenData?.userProfile?.TransactionFee &&
                paymentFormik?.values?.transactionFeeDetails &&
                parseFloat(paymentFormik?.values?.transactionFeeDetails?.feeAmount || 0) > 0 && (
                  <div
                    style={{
                      backgroundColor: "var(--bg-white-color)",
                      border: "1px solid #e2e8f0",
                      padding: "20px",
                      marginBottom: "24px",
                    }}
                  >
                    <Typography
                      style={{
                        color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                        fontWeight: 500,
                        fontSize: "14px",
                        marginBottom: "16px",
                      }}
                    >
                      Fee Breakdown
                    </Typography>
                    <div style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          style={{
                            color: "#64748b",
                            fontSize: "13px",
                            fontWeight: 400,
                          }}
                        >
                          Subtotal
                        </Typography>
                        <Typography
                          style={{
                            color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        >
                          $
                          {
                            paymentFormik?.values?.transactionFeeDetails
                              ?.originalAmount
                          }
                        </Typography>
                      </div>
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          style={{
                            color: "#64748b",
                            fontSize: "13px",
                            fontWeight: 400,
                          }}
                        >
                          Processing Fee (
                          {
                            paymentFormik?.values?.transactionFeeDetails
                              ?.feePercentage
                          }
                          %)
                        </Typography>
                        <Typography
                          style={{
                            color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        >
                          $
                          {
                            paymentFormik?.values?.transactionFeeDetails
                              ?.feeAmount
                          }
                        </Typography>
                      </div>
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #e2e8f0",
                        paddingTop: "16px",
                        marginTop: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          style={{
                            color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                            fontSize: "15px",
                            fontWeight: 600,
                          }}
                        >
                          Total Amount
                        </Typography>
                        <Typography
                          style={{
                            color: isDonationMode ? "var(--border-donor-color)" : "var(--text-blue-color)",
                            fontSize: "18px",
                            fontWeight: 600,
                          }}
                        >
                          $
                          {
                            paymentFormik?.values?.transactionFeeDetails
                              ?.totalAmount
                          }
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}

              {/* Simple Pay Button */}
              <div style={{ marginBottom: "32px" }}>
                {buttonLoader ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <CircularProgress size={40} color="inherit" />
                  </div>
                ) : (
                  <Button
                    onClick={paymentFormik?.handleSubmit}
                    style={{
                      width: "100%",
                      backgroundColor: isDonationMode
                        ? "var(--bg-green-color)"
                        : "var(--bg-blue-color)",
                      color: "var(--text-white-color)",
                      border: "none",
                      padding: "12px 24px",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: !creditCardEnabled || !selectedCard ? "not-allowed" : "pointer",
                      opacity: !creditCardEnabled || !selectedCard ? 0.6 : 1,
                      textTransform: "none",
                    }}
                    disabled={!creditCardEnabled || !selectedCard}
                  >
                    Pay ${new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(
                      parseFloat(
                        paymentFormik?.values?.transactionFeeDetails
                          ?.totalAmount || tokenData?.recurring?.amount || 0
                      )
                    )}
                  </Button>
                )}
              </div>

              {/* Simple Footer */}
              {tokenData?.company && (
                <div
                  style={{
                    textAlign: "center",
                    paddingTop: "32px",
                    borderTop: "1px solid #e2e8f0",
                    padding: "24px 0",
                    margin: "0 -40px",
                  }}
                >
                  <Typography
                    style={{
                      color: "var(--text-blue-color)",
                      fontSize: "13px",
                      marginBottom: "8px",
                      fontWeight: 500,
                    }}
                  >
                    {tokenData?.company?.companyName || "Company Name"}
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    {tokenData?.company?.phoneNumber && (
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "12px",
                        }}
                      >
                        <a
                          href={`tel:${tokenData.company.phoneNumber}`}
                          style={{
                            color: "var(--text-blue-color)",
                            textDecoration: "none",
                            fontWeight: 400,
                          }}
                        >
                          {tokenData.company.phoneNumber}
                        </a>
                      </Typography>
                    )}
                    {tokenData?.company?.EmailAddress && (
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "12px",
                        }}
                      >
                        <a
                          href={`mailto:${tokenData.company.EmailAddress}`}
                          style={{
                            color: "var(--text-blue-color)",
                            textDecoration: "none",
                            fontWeight: 400,
                          }}
                        >
                          {tokenData.company.EmailAddress}
                        </a>
                      </Typography>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                
                border: "2px solid var(--border-blue-color)",
                padding: "48px",
              }}
            >
              <Typography
                style={{
                  color: "var(--text-blue-color)",
                  fontWeight: 700,
                  fontSize: "28px",
                  marginBottom: "40px",
                  textAlign: "center",
                  paddingBottom: "24px",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                Payment Details
              </Typography>
              <div
                style={{
                  marginBottom: "20px",
                  paddingBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Customer Name
                </Typography>
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {`${tokenData?.customer?.FirstName || ""} ${
                    tokenData?.customer?.LastName || ""
                  }` || "-"}
                </Typography>
              </div>
              <div
                style={{
                  marginBottom: "20px",
                  paddingBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Payment Frequency
                </Typography>
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {CycleData()}
                </Typography>
              </div>
              <div
                style={{
                  marginBottom: "20px",
                  paddingBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Next Due Date
                </Typography>
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {tokenData?.recurring?.nextDueDate
                    ? formatDateWithUserTimezone(tokenData?.recurring?.nextDueDate, {
                        dateFormat: dateFormat,
                        includeSeconds: false
                      })
                    : "-"}
                </Typography>
              </div>
              <div
                style={{
                  marginBottom: "24px",
                  paddingBottom: "24px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Description
                </Typography>
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {tokenData?.recurring?.description || "-"}
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontWeight: 600,
                    fontSize: "18px",
                  }}
                >
                  Total Amount
                </Typography>
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontWeight: 700,
                    fontSize: "24px",
                  }}
                >
                  {`$${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(
                    (parseFloat(tokenData?.recurring?.amount) || 0) +
                      transactionFee
                  )}`}
                </Typography>
              </div>
            </div>
          )}
        </Grid>
      </Grid>
    );
};

export default InvoiceLivePayment;
